/**
 * Level8Scene - 关卡8：电磁感应
 * @description 教学目标：理解法拉第电磁感应定律 ε = -dΦ/dt
 * 玩家通过移动导体穿越磁场来产生感应电流
 */

import Phaser from 'phaser';
import { Vec2 } from 'planck';
import type { LevelConfig } from '@/levels/LevelConfig.ts';
import { TutorialUI } from '@/ui/TutorialUI.ts';
import { getTutorial } from '@/data/tutorials.ts';
import { getGameState } from '@/core/GameState.ts';
import { EMField } from '@/physics/EMField.ts';
import { FieldRenderer } from '@/physics/FieldRenderer.ts';

// 关卡8配置
interface Level8SpecificConfig {
  magneticFieldStrength: number;
  magneticFieldRadius: number;
  magneticFieldCenterX: number;
  magneticFieldCenterY: number;
  conductorLength: number;
  minVelocityThreshold: number;
  targetInducedVoltage: number;
  voltageTolerance: number;
  formula: string;
  hint: string;
}

// 游戏状态
type GameState = 'idle' | 'running' | 'success' | 'failed';

// 游戏尺寸常量
const GAME_WIDTH = 800;
const GAME_HEIGHT = 600;

/**
 * Level8Scene 类
 * 关卡8场景，实现电磁感应教学
 */
export class Level8Scene extends Phaser.Scene {
  // 关卡配置
  private levelConfig: LevelConfig | null = null;
  private levelSpecific: Level8SpecificConfig | null = null;

  // 游戏状态
  private gameState: GameState = 'idle';
  private elapsedTime: number = 0;
  private inducedVoltage: number = 0;
  private maxInducedVoltage: number = 0;
  private totalInducedEnergy: number = 0;

  // 游戏对象
  private conductor: Phaser.GameObjects.Graphics | null = null;
  private conductorX: number = 100;
  private conductorY: number = 300;
  private conductorVelocityX: number = 0;
  private conductorVelocityY: number = 0;
  private isDragging: boolean = false;

  // 磁场
  private emField: EMField | null = null;
  private fieldRenderer: FieldRenderer | null = null;

  // UI 元素
  private hintText: Phaser.GameObjects.Text | null = null;
  private voltageDisplay: Phaser.GameObjects.Text | null = null;
  private timerText: Phaser.GameObjects.Text | null = null;
  private startButton: Phaser.GameObjects.Graphics | null = null;
  private resultText: Phaser.GameObjects.Text | null = null;

  // 感应电流可视化
  private currentFlowGraphics: Phaser.GameObjects.Graphics | null = null;

  // 路径轨迹
  private pathGraphics: Phaser.GameObjects.Graphics | null = null;

  // 教程组件
  private tutorialUI: TutorialUI | null = null;

  constructor() {
    super({ key: 'Level8Scene' });
  }

  preload(): void {
    // 加载关卡配置
    this.load.json('level-8', 'src/levels/levels/level-8.json');
  }

  create(): void {
    // 加载关卡配置
    this.levelConfig = this.cache.json.get('level-8') as LevelConfig;
    
    if (this.levelConfig && this.levelConfig.levelSpecific) {
      this.levelSpecific = this.levelConfig.levelSpecific as unknown as Level8SpecificConfig;
    }

    // 创建背景
    this.createBackground();

    // 创建电磁场
    this.createEMField();

    // 创建游戏场景
    this.createGameScene();

    // 创建 UI
    this.createUI();

    // 设置输入事件
    this.setupInputEvents();

    // 显示教程
    this.showTutorial();
  }

  override update(_time: number, delta: number): void {
    if (this.gameState !== 'running') return;

    // 更新时间
    this.elapsedTime += delta / 1000;
    this.updateTimerDisplay();

    // 计算感应电压（法拉第定律：ε = B·L·v）
    this.calculateInducedVoltage();

    // 更新导体位置（如果不是拖拽状态，继续惯性运动）
    if (!this.isDragging) {
      const dt = delta / 1000;
      // 简单惯性衰减
      this.conductorVelocityX *= 0.98;
      this.conductorVelocityY *= 0.98;
      
      this.conductorX += this.conductorVelocityX * dt;
      this.conductorY += this.conductorVelocityY * dt;
    }

    // 更新导体渲染
    this.updateConductorPosition();

    // 绘制路径轨迹
    this.drawPath();

    // 检测胜利/失败
    this.checkGameEnd();
  }

  /**
   * 创建背景
   */
  private createBackground(): void {
    // 深色背景
    this.cameras.main.setBackgroundColor('#1a1a2e');

    // 添加网格线作为参考
    const gridGraphics = this.add.graphics();
    gridGraphics.lineStyle(1, 0x2a2a4a, 0.3);

    // 垂直网格线
    for (let x = 0; x <= GAME_WIDTH; x += 50) {
      gridGraphics.beginPath();
      gridGraphics.moveTo(x, 0);
      gridGraphics.lineTo(x, GAME_HEIGHT);
      gridGraphics.strokePath();
    }

    // 水平网格线
    for (let y = 0; y <= GAME_HEIGHT; y += 50) {
      gridGraphics.beginPath();
      gridGraphics.moveTo(0, y);
      gridGraphics.lineTo(GAME_WIDTH, y);
      gridGraphics.strokePath();
    }
  }

  /**
   * 创建电磁场
   */
  private createEMField(): void {
    const centerX = this.levelSpecific?.magneticFieldCenterX ?? 400;
    const centerY = this.levelSpecific?.magneticFieldCenterY ?? 300;
    const strength = this.levelSpecific?.magneticFieldStrength ?? 5.0;

    this.emField = new EMField(1e-3);

    // 添加磁场源（垂直于屏幕的磁场）
    this.emField.addMagneticSource({
      id: 'main-magnetic-field',
      position: Vec2(centerX, centerY),
      strength: strength,
      radius: this.levelSpecific?.magneticFieldRadius ?? 150,
    });

    // 创建场线渲染器
    this.fieldRenderer = new FieldRenderer(
      this,
      this.emField,
      {
        magneticFieldColor: 0x9b59b6,
        lineWidth: 2,
      },
      GAME_HEIGHT,
      1
    );

    // 渲染磁感线
    this.fieldRenderer.render();
  }

  /**
   * 创建游戏场景
   */
  private createGameScene(): void {
    const centerX = this.levelSpecific?.magneticFieldCenterX ?? 400;
    const centerY = this.levelSpecific?.magneticFieldCenterY ?? 300;
    const radius = this.levelSpecific?.magneticFieldRadius ?? 150;

    // 磁场区域可视化
    const magneticZoneGraphics = this.add.graphics();
    magneticZoneGraphics.fillStyle(0x9b59b6, 0.15);
    magneticZoneGraphics.fillCircle(centerX, centerY, radius);
    magneticZoneGraphics.lineStyle(2, 0x9b59b6, 0.8);
    magneticZoneGraphics.strokeCircle(centerX, centerY, radius);

    // 磁场区域标签
    this.add.text(centerX, centerY - radius - 20, '磁场区域 (B ⊙)', {
      fontSize: '16px',
      color: '#9b59b6',
      fontFamily: 'Arial',
    }).setOrigin(0.5);

    // 磁场方向说明（垂直于屏幕向上）
    const directionMarker = this.add.graphics();
    directionMarker.fillStyle(0x9b59b6, 1);
    // 绘制 ⊙ 符号（表示磁场垂直向上）
    directionMarker.fillCircle(centerX, centerY, 8);
    directionMarker.lineStyle(2, 0xffffff);
    directionMarker.strokeCircle(centerX, centerY, 8);

    // 起点标记
    const startMarker = this.add.graphics();
    startMarker.fillStyle(0xff6b6b, 0.3);
    startMarker.fillRect(50, centerY - 50, 50, 100);
    startMarker.lineStyle(2, 0xff6b6b);
    startMarker.strokeRect(50, centerY - 50, 50, 100);

    this.add.text(75, centerY + 70, '起点', {
      fontSize: '14px',
      color: '#ff6b6b',
      fontFamily: 'Arial',
    }).setOrigin(0.5);

    // 终点区域
    const goalX = this.levelConfig?.goal?.position?.x ?? 700;
    const goalY = this.levelConfig?.goal?.position?.y ?? centerY;

    const goalZone = this.add.graphics();
    goalZone.fillStyle(0x4ade80, 0.3);
    goalZone.fillRect(goalX - 30, goalY - 50, 60, 100);
    goalZone.lineStyle(3, 0x4ade80);
    goalZone.strokeRect(goalX - 30, goalY - 50, 60, 100);

    this.add.text(goalX, goalY + 70, '终点', {
      fontSize: '14px',
      color: '#4ade80',
      fontFamily: 'Arial',
    }).setOrigin(0.5);

    // 创建导体
    this.createConductor();

    // 路径轨迹
    this.pathGraphics = this.add.graphics();

    // 感应电流可视化
    this.currentFlowGraphics = this.add.graphics();
    this.currentFlowGraphics.setDepth(10);
  }

  /**
   * 创建导体（金属棒）
   */
  private createConductor(): void {
    this.conductorX = 100;
    this.conductorY = GAME_HEIGHT / 2;

    this.conductor = this.add.graphics();
    this.drawConductor(this.conductorX, this.conductorY, 0);

    // 设置导体可交互（拖拽）
    this.conductor.setInteractive(
      new Phaser.Geom.Rectangle(this.conductorX - 20, this.conductorY - 40, 40, 80),
      Phaser.Geom.Rectangle.Contains
    );
  }

  /**
   * 绘制导体（金属棒）
   */
  private drawConductor(x: number, y: number, voltage: number): void {
    if (!this.conductor) return;

    const conductorLength = this.levelSpecific?.conductorLength ?? 80;

    this.conductor.clear();

    // 根据感应电压改变颜色（电压越高颜色越亮）
    const intensity = Math.min(1, Math.abs(voltage) / 100);

    // 混合颜色
    const r = Math.floor((0xcc + (0xff - 0xcc) * intensity));
    const g = Math.floor((0xcc + (0xd7 - 0xcc) * intensity));
    const b = Math.floor((0xcc + (0x00 - 0xcc) * intensity));
    const mixedColor = (r << 16) | (g << 8) | b;

    // 导体主体（金属棒）
    this.conductor.fillStyle(mixedColor, 1);
    this.conductor.fillRect(x - 8, y - conductorLength / 2, 16, conductorLength);
    this.conductor.lineStyle(2, 0xffffff);
    this.conductor.strokeRect(x - 8, y - conductorLength / 2, 16, conductorLength);

    // 导体两端标记（表示连接点）
    this.conductor.fillStyle(voltage > 0 ? 0xff6b6b : 0x4ecdc4, 1);
    this.conductor.fillCircle(x, y - conductorLength / 2, 6);
    this.conductor.fillCircle(x, y + conductorLength / 2, 6);

    // 感应电流方向箭头（当有电压时）
    if (Math.abs(voltage) > 10) {
      this.drawCurrentArrow(x, y, voltage);
    }

    // 更新交互区域
    this.conductor.setInteractive(
      new Phaser.Geom.Rectangle(x - 20, y - conductorLength / 2, 40, conductorLength),
      Phaser.Geom.Rectangle.Contains
    );
  }

  /**
   * 绘制感应电流方向箭头
   */
  private drawCurrentArrow(x: number, y: number, voltage: number): void {
    if (!this.currentFlowGraphics) return;

    this.currentFlowGraphics.clear();
    
    const conductorLength = this.levelSpecific?.conductorLength ?? 80;
    const arrowLength = Math.min(30, Math.abs(voltage) / 5);

    // 根据电压方向决定电流流向（右手定则）
    // 电压为正时电流向上，电压为负时电流向下
    const direction = voltage > 0 ? -1 : 1;

    this.currentFlowGraphics.lineStyle(3, 0xffd700, 1);
    this.currentFlowGraphics.beginPath();
    
    // 从导体中心开始
    const startY = y;
    const endY = startY + direction * arrowLength;
    
    this.currentFlowGraphics.moveTo(x, startY);
    this.currentFlowGraphics.lineTo(x, endY);
    
    // 箭头头部
    this.currentFlowGraphics.moveTo(x - 5, endY - direction * 5);
    this.currentFlowGraphics.lineTo(x, endY);
    this.currentFlowGraphics.lineTo(x + 5, endY - direction * 5);
    
    this.currentFlowGraphics.strokePath();

    // 电流强度指示（发光效果）
    const glowIntensity = Math.min(0.8, Math.abs(voltage) / 100);
    this.currentFlowGraphics.fillStyle(0xffd700, glowIntensity);
    this.currentFlowGraphics.fillCircle(x, y, conductorLength / 2 + 5);
  }

  /**
   * 计算感应电压
   * 法拉第定律：ε = -dΦ/dt = -B·L·v
   */
  private calculateInducedVoltage(): void {
    const centerX = this.levelSpecific?.magneticFieldCenterX ?? 400;
    const centerY = this.levelSpecific?.magneticFieldCenterY ?? 300;
    const radius = this.levelSpecific?.magneticFieldRadius ?? 150;
    const magneticStrength = this.levelSpecific?.magneticFieldStrength ?? 5.0;
    const conductorLength = this.levelSpecific?.conductorLength ?? 80;

    // 检查导体是否在磁场区域内
    const dx = this.conductorX - centerX;
    const dy = this.conductorY - centerY;
    const distance = Math.sqrt(dx * dx + dy * dy);

    if (distance <= radius) {
      // 在磁场区域内，计算感应电压
      // ε = B × L × v（速度垂直于磁场的分量）

      // 计算速度方向与磁场边界的垂直程度
      // 假设磁场边界方向是径向，导体切割磁感线的效果取决于径向速度
      const radialVelocity = (dx * this.conductorVelocityX + dy * this.conductorVelocityY) / distance;
      
      // 感应电压 = B × L × v_radial
      const magneticStrengthFactor = magneticStrength * (1 - distance / radius); // 磁场强度随距离衰减
      this.inducedVoltage = magneticStrengthFactor * conductorLength * Math.abs(radialVelocity) * 10;
      
      // 记录最大感应电压
      if (this.inducedVoltage > this.maxInducedVoltage) {
        this.maxInducedVoltage = this.inducedVoltage;
      }

      // 累积感应能量
      this.totalInducedEnergy += this.inducedVoltage * 0.01;
    } else {
      // 在磁场区域外，感应电压为零
      this.inducedVoltage = 0;
    }

    // 更新电压显示
    this.updateVoltageDisplay();
    
    // 更新导体颜色
    this.drawConductor(this.conductorX, this.conductorY, this.inducedVoltage);
  }

  /**
   * 更新导体位置
   */
  private updateConductorPosition(): void {
    // 边界检测
    const minX = 30;
    const maxX = GAME_WIDTH - 30;
    const minY = 100;
    const maxY = GAME_HEIGHT - 100;

    this.conductorX = Math.max(minX, Math.min(maxX, this.conductorX));
    this.conductorY = Math.max(minY, Math.min(maxY, this.conductorY));

    this.drawConductor(this.conductorX, this.conductorY, this.inducedVoltage);
  }

  /**
   * 绘制路径轨迹
   */
  private drawPath(): void {
    if (!this.pathGraphics) return;

    // 添加当前位置到轨迹
    this.pathGraphics.fillStyle(0xffff00, 0.3);
    this.pathGraphics.fillCircle(this.conductorX, this.conductorY, 3);
  }

  /**
   * 创建 UI
   */
  private createUI(): void {
    // 公式显示
    this.add.text(GAME_WIDTH / 2, 50,
      '法拉第电磁感应定律：ε = -B·L·v', {
      fontSize: '24px',
      color: '#ffffff',
      fontFamily: 'Arial',
      fontStyle: 'bold',
    }).setOrigin(0.5);

    // 公式解释
    this.add.text(GAME_WIDTH / 2, 80,
      '变化的磁通量产生感应电动势', {
      fontSize: '14px',
      color: '#888888',
      fontFamily: 'Arial',
    }).setOrigin(0.5);

    // 提示文本
    this.hintText = this.add.text(GAME_WIDTH / 2, 110,
      this.levelSpecific?.hint ?? '拖拽导体穿越磁场区域！', {
      fontSize: '16px',
      color: '#888888',
      fontFamily: 'Arial',
    }).setOrigin(0.5);

    // 感应电压显示
    const voltageBg = this.add.graphics();
    voltageBg.fillStyle(0x2a2a4a, 1);
    voltageBg.fillRect(50, 500, 300, 80);
    voltageBg.lineStyle(2, 0x4a4a6a);
    voltageBg.strokeRect(50, 500, 300, 80);

    this.add.text(200, 520, '感应电压 (ε)', {
      fontSize: '16px',
      color: '#ffd700',
      fontFamily: 'Arial',
      fontStyle: 'bold',
    }).setOrigin(0.5);

    this.voltageDisplay = this.add.text(200, 550, '0.0 V', {
      fontSize: '28px',
      color: '#ffd700',
      fontFamily: 'Arial',
      fontStyle: 'bold',
    }).setOrigin(0.5);

    // 目标电压提示
    const targetVoltage = this.levelSpecific?.targetInducedVoltage ?? 100;
    this.add.text(200, 575, `目标: ≥${targetVoltage}V (峰值)`, {
      fontSize: '12px',
      color: '#888888',
      fontFamily: 'Arial',
    }).setOrigin(0.5);

    // 计时器显示
    this.timerText = this.add.text(GAME_WIDTH - 150, 520, '时间: 0.00 秒', {
      fontSize: '20px',
      color: '#ffffff',
      fontFamily: 'Arial',
    }).setOrigin(0.5);

    // 最大电压记录
    this.add.text(GAME_WIDTH - 150, 550, `峰值电压: 0.0 V`, {
      fontSize: '14px',
      color: '#4ecdc4',
      fontFamily: 'Arial',
    }).setOrigin(0.5);

    // 创建速度控制按钮
    this.createVelocityControls();

    // 开始按钮
    this.createStartButton();

    // 结果文本（初始隐藏）
    this.resultText = this.add.text(GAME_WIDTH / 2, GAME_HEIGHT / 2, '', {
      fontSize: '32px',
      color: '#4ade80',
      fontFamily: 'Arial',
      fontStyle: 'bold',
    }).setOrigin(0.5).setVisible(false);

    // 物理原理说明面板
    this.createPhysicsExplanation();
  }

  /**
   * 创建速度控制按钮
   */
  private createVelocityControls(): void {
    const startX = 500;
    const y = 540;

    // 预设速度按钮
    const presets = [
      { label: '慢速', velocity: 50 },
      { label: '中速', velocity: 100 },
      { label: '高速', velocity: 200 },
    ];

    presets.forEach((preset, i) => {
      const x = startX + i * 80;
      
      const btn = this.add.graphics();
      btn.fillStyle(0x4a4a6a, 1);
      btn.fillRect(x - 30, y - 20, 60, 40);
      btn.lineStyle(1, 0x6a6a8a);
      btn.strokeRect(x - 30, y - 20, 60, 40);

      this.add.text(x, y, preset.label, {
        fontSize: '14px',
        color: '#ffffff',
        fontFamily: 'Arial',
      }).setOrigin(0.5);

      btn.setInteractive(
        new Phaser.Geom.Rectangle(x - 30, y - 20, 60, 40),
        Phaser.Geom.Rectangle.Contains
      );

      btn.on('pointerdown', () => {
        if (this.gameState === 'running') {
          // 设置导体速度（水平方向）
          this.conductorVelocityX = preset.velocity;
          this.conductorVelocityY = 0;
        }
      });

      btn.on('pointerover', () => {
        btn.clear();
        btn.fillStyle(0x5a5a7a, 1);
        btn.fillRect(x - 30, y - 20, 60, 40);
        btn.strokeRect(x - 30, y - 20, 60, 40);
      });

      btn.on('pointerout', () => {
        btn.clear();
        btn.fillStyle(0x4a4a6a, 1);
        btn.fillRect(x - 30, y - 20, 60, 40);
        btn.strokeRect(x - 30, y - 20, 60, 40);
      });
    });
  }

  /**
   * 创建物理原理说明面板
   */
  private createPhysicsExplanation(): void {
    const panelX = 650;
    const panelY = 20;
    const panelWidth = 140;
    const panelHeight = 100;

    const panel = this.add.graphics();
    panel.fillStyle(0x2a2a4a, 0.8);
    panel.fillRect(panelX, panelY, panelWidth, panelHeight);
    panel.lineStyle(1, 0x4a4a6a);
    panel.strokeRect(panelX, panelY, panelWidth, panelHeight);

    // 物理原理
    this.add.text(panelX + panelWidth / 2, panelY + 15, '物理原理', {
      fontSize: '12px',
      color: '#ffffff',
      fontFamily: 'Arial',
      fontStyle: 'bold',
    }).setOrigin(0.5);

    this.add.text(panelX + panelWidth / 2, panelY + 35, 'B: 磁场强度', {
      fontSize: '10px',
      color: '#888888',
      fontFamily: 'Arial',
    }).setOrigin(0.5);

    this.add.text(panelX + panelWidth / 2, panelY + 50, 'L: 导体长度', {
      fontSize: '10px',
      color: '#888888',
      fontFamily: 'Arial',
    }).setOrigin(0.5);

    this.add.text(panelX + panelWidth / 2, panelY + 65, 'v: 切割速度', {
      fontSize: '10px',
      color: '#888888',
      fontFamily: 'Arial',
    }).setOrigin(0.5);

    this.add.text(panelX + panelWidth / 2, panelY + 85, 'ε = B × L × v', {
      fontSize: '12px',
      color: '#ffd700',
      fontFamily: 'Arial',
      fontStyle: 'bold',
    }).setOrigin(0.5);
  }

  /**
   * 创建开始按钮
   */
  private createStartButton(): void {
    this.startButton = this.add.graphics();
    this.startButton.fillStyle(0x4ade80, 1);
    this.startButton.fillRect(680, 450, 100, 40);
    this.startButton.lineStyle(2, 0x4ade80);
    this.startButton.strokeRect(680, 450, 100, 40);

    this.add.text(730, 470, '开始', {
      fontSize: '18px',
      color: '#ffffff',
      fontFamily: 'Arial',
      fontStyle: 'bold',
    }).setOrigin(0.5);

    this.startButton.setInteractive(
      new Phaser.Geom.Rectangle(680, 450, 100, 40),
      Phaser.Geom.Rectangle.Contains
    );
    this.startButton.on('pointerdown', () => this.startGame());

    this.startButton.on('pointerover', () => {
      this.startButton?.clear();
      this.startButton?.fillStyle(0x5aee90, 1);
      this.startButton?.fillRect(680, 450, 100, 40);
      this.startButton?.strokeRect(680, 450, 100, 40);
    });

    this.startButton.on('pointerout', () => {
      this.startButton?.clear();
      this.startButton?.fillStyle(0x4ade80, 1);
      this.startButton?.fillRect(680, 450, 100, 40);
      this.startButton?.strokeRect(680, 450, 100, 40);
    });
  }

  /**
   * 设置输入事件
   */
  private setupInputEvents(): void {
    // 鼠标拖拽导体
    this.input.on('pointerdown', (pointer: Phaser.Input.Pointer) => {
      if (this.gameState !== 'running') return;
      
      // 检查是否点击了导体
      const distance = Math.sqrt(
        Math.pow(pointer.x - this.conductorX, 2) +
        Math.pow(pointer.y - this.conductorY, 2)
      );
      
      if (distance < 50) {
        this.isDragging = true;
        this.conductorVelocityX = 0;
        this.conductorVelocityY = 0;
      }
    });

    this.input.on('pointermove', (pointer: Phaser.Input.Pointer) => {
      if (!this.isDragging || this.gameState !== 'running') return;
      
      // 计算移动速度
      const dx = pointer.x - this.conductorX;
      const dy = pointer.y - this.conductorY;
      
      // 更新导体位置
      this.conductorX = pointer.x;
      this.conductorY = pointer.y;
      
      // 记录瞬时速度（用于感应电压计算）
      // 下一次 update 时会使用这个速度
      this.conductorVelocityX = dx * 60; // 假设 60fps
      this.conductorVelocityY = dy * 60;
    });

    this.input.on('pointerup', () => {
      if (this.isDragging) {
        this.isDragging = false;
      }
    });

    // 键盘输入
    this.input.keyboard?.on('keydown', (event: KeyboardEvent) => {
      if (this.gameState !== 'running') return;

      const speed = 150;
      
      switch (event.key) {
        case 'ArrowLeft':
          this.conductorVelocityX = -speed;
          this.conductorVelocityY = 0;
          break;
        case 'ArrowRight':
          this.conductorVelocityX = speed;
          this.conductorVelocityY = 0;
          break;
        case 'ArrowUp':
          this.conductorVelocityX = 0;
          this.conductorVelocityY = -speed;
          break;
        case 'ArrowDown':
          this.conductorVelocityX = 0;
          this.conductorVelocityY = speed;
          break;
        case 'r':
        case 'R':
          this.resetGame();
          break;
      }
    });
  }

  /**
   * 更新电压显示
   */
  private updateVoltageDisplay(): void {
    if (this.voltageDisplay) {
      this.voltageDisplay.setText(`${this.inducedVoltage.toFixed(1)} V`);
      
      // 根据电压值改变颜色
      const targetVoltage = this.levelSpecific?.targetInducedVoltage ?? 100;
      if (this.inducedVoltage >= targetVoltage) {
        this.voltageDisplay.setColor('#4ade80');
      } else if (this.inducedVoltage > 50) {
        this.voltageDisplay.setColor('#ffd700');
      } else {
        this.voltageDisplay.setColor('#888888');
      }
    }

    // 更新峰值电压显示
    this.add.text(GAME_WIDTH - 150, 550, `峰值电压: ${this.maxInducedVoltage.toFixed(1)} V`, {
      fontSize: '14px',
      color: this.maxInducedVoltage >= (this.levelSpecific?.targetInducedVoltage ?? 100) ? '#4ade80' : '#4ecdc4',
      fontFamily: 'Arial',
    }).setOrigin(0.5);
  }

  /**
   * 更新计时器显示
   */
  private updateTimerDisplay(): void {
    if (this.timerText) {
      this.timerText.setText(`时间: ${this.elapsedTime.toFixed(2)} 秒`);
    }
  }

  /**
   * 开始游戏
   */
  private startGame(): void {
    if (this.gameState !== 'idle') return;

    this.gameState = 'running';
    this.elapsedTime = 0;
    this.inducedVoltage = 0;
    this.maxInducedVoltage = 0;
    this.totalInducedEnergy = 0;
    this.conductorX = 100;
    this.conductorY = GAME_HEIGHT / 2;
    this.conductorVelocityX = 0;
    this.conductorVelocityY = 0;

    // 清除路径轨迹
    if (this.pathGraphics) {
      this.pathGraphics.clear();
    }

    // 隐藏结果
    this.resultText?.setVisible(false);

    // 更改按钮状态
    this.updateStartButtonState('running');
  }

  /**
   * 更新开始按钮状态
   */
  private updateStartButtonState(state: GameState): void {
    if (!this.startButton) return;

    this.startButton.clear();

    if (state === 'running') {
      this.startButton.fillStyle(0x6a6a8a, 1);
      this.startButton.fillRect(680, 450, 100, 40);
    } else if (state === 'idle') {
      this.startButton.fillStyle(0x4ade80, 1);
      this.startButton.fillRect(680, 450, 100, 40);
    }
    this.startButton.strokeRect(680, 450, 100, 40);
  }

  /**
   * 检测游戏结束
   */
  private checkGameEnd(): void {
    const targetVoltage = this.levelSpecific?.targetInducedVoltage ?? 100;
    const goalX = this.levelConfig?.goal?.position?.x ?? 700;

    // 检测是否达到目标电压并到达终点
    if (this.conductorX >= goalX - 30 && this.maxInducedVoltage >= targetVoltage) {
      this.gameState = 'success';
      this.conductorVelocityX = 0;
      this.conductorVelocityY = 0;
      this.showSuccessResult();
      this.updateStartButtonState('idle');
    }

    // 检测是否到达终点但电压不足
    if (this.conductorX >= goalX - 30 && this.maxInducedVoltage < targetVoltage) {
      this.gameState = 'failed';
      this.conductorVelocityX = 0;
      this.conductorVelocityY = 0;
      this.showFailResult('到达终点，但峰值电压不足');
      this.updateStartButtonState('idle');
    }

    // 检测是否超出边界
    if (this.conductorX > GAME_WIDTH || this.conductorX < 30 ||
        this.conductorY > GAME_HEIGHT - 50 || this.conductorY < 50) {
      // 只在超出右边界时失败
      if (this.conductorX > GAME_WIDTH) {
        this.gameState = 'failed';
        this.conductorVelocityX = 0;
        this.conductorVelocityY = 0;
        this.showFailResult('导体超出边界！');
        this.updateStartButtonState('idle');
      }
    }
  }

  /**
   * 显示成功结果
   */
  private showSuccessResult(): void {
    const gameState = getGameState();
    gameState.completeLevel(8, this.elapsedTime);

    this.resultText?.setText(`🎉 成功！峰值电压 ${this.maxInducedVoltage.toFixed(1)}V`)
      .setColor('#4ade80')
      .setVisible(true);

    this.hintText?.setText(`法拉第定律验证成功！ε = B × L × v`);

    this.createCompletionButtons();
  }

  private createCompletionButtons(): void {
    const y = GAME_HEIGHT / 2 + 80;

    const nextBtn = this.add.text(GAME_WIDTH / 2 - 80, y, '下一关', {
      fontSize: '18px',
      color: '#4ade80',
      fontFamily: 'Arial',
      backgroundColor: '#333333',
      padding: { x: 15, y: 8 },
    }).setOrigin(0.5).setInteractive();

    nextBtn.on('pointerdown', () => {
      const gameState = getGameState();
      if (gameState.isLevelUnlocked(9)) {
        this.scene.start('Level9Scene');
      } else {
        this.scene.start('LevelSelectScene');
      }
    });

    const backBtn = this.add.text(GAME_WIDTH / 2 + 80, y, '返回', {
      fontSize: '18px',
      color: '#888888',
      fontFamily: 'Arial',
      backgroundColor: '#333333',
      padding: { x: 15, y: 8 },
    }).setOrigin(0.5).setInteractive();

    backBtn.on('pointerdown', () => {
      this.scene.start('LevelSelectScene');
    });
  }

  /**
   * 显示失败结果
   */
  private showFailResult(reason: string): void {
    this.resultText?.setText(`❌ ${reason}`)
      .setColor('#ff6b6b')
      .setVisible(true);

    const targetVoltage = this.levelSpecific?.targetInducedVoltage ?? 100;
    this.hintText?.setText(`尝试提高导体穿越磁场的速度！目标 ≥${targetVoltage}V`);
  }

  /**
   * 重置游戏
   */
  private resetGame(): void {
    this.gameState = 'idle';
    this.elapsedTime = 0;
    this.inducedVoltage = 0;
    this.maxInducedVoltage = 0;
    this.totalInducedEnergy = 0;
    this.conductorX = 100;
    this.conductorY = GAME_HEIGHT / 2;
    this.conductorVelocityX = 0;
    this.conductorVelocityY = 0;

    // 清除路径
    if (this.pathGraphics) {
      this.pathGraphics.clear();
    }

    // 清除电流可视化
    if (this.currentFlowGraphics) {
      this.currentFlowGraphics.clear();
    }

    // 隐藏结果
    this.resultText?.setVisible(false);

    // 重置导体位置
    this.drawConductor(this.conductorX, this.conductorY, 0);

    // 重置计时器
    this.updateTimerDisplay();

    // 重置电压显示
    this.voltageDisplay?.setText('0.0 V');
    this.voltageDisplay?.setColor('#888888');

    // 重置提示
    this.hintText?.setText(this.levelSpecific?.hint ?? '拖拽导体穿越磁场区域！');

    // 更新按钮状态
    this.updateStartButtonState('idle');
  }

  /**
   * 显示教程
   */
  private showTutorial(): void {
    const tutorialData = getTutorial('level-8');
    if (!tutorialData) return;

    this.tutorialUI = new TutorialUI(this, {
      levelNumber: 8,
      tutorialData,
      onStart: () => {
        this.tutorialUI?.destroy();
        this.tutorialUI = null;
      },
      onSkip: () => {
        this.tutorialUI?.destroy();
        this.tutorialUI = null;
      },
    });
  }

  /**
   * 获取当前游戏状态
   */
  getGameState(): GameState {
    return this.gameState;
  }

  /**
   * 获取当前时间
   */
  getElapsedTime(): number {
    return this.elapsedTime;
  }

  /**
   * 获取当前感应电压
   */
  getInducedVoltage(): number {
    return this.inducedVoltage;
  }

  /**
   * 获取最大感应电压
   */
  getMaxInducedVoltage(): number {
    return this.maxInducedVoltage;
  }
}